import { getGrampsApiBase } from '../grampsAuth';
import type { components } from './gramps-openapi';

export type ChatMessage = components['schemas']['ChatMessage'];
export type ChatResponse = components['schemas']['ChatResponse'];
export type ChatTaskStatus = components['schemas']['TaskStatus'];

export class ChatError extends Error {}

async function readErrorMessage(response: Response): Promise<string> {
    try {
        const body = await response.json() as { error?: { message?: string } };
        if (body?.error?.message) return body.error.message;
    } catch {
        // response body wasn't JSON (or was empty) -- fall through to the generic message below.
    }
    return response.statusText || `HTTP ${response.status}`;
}

type ChatTaskHandle = { id: string; href: string };

/**
 * Dispatches the query as a background Celery task (`background=true`) instead of
 * waiting for the synchronous response: the LLM call plus however many tool calls
 * the agent makes to look through the tree can easily exceed nginx's default
 * proxy read timeout (60s, unset in nginx_proxy.conf) on this 2-CPU box, so a
 * long chat would otherwise fail with a proxy timeout rather than a clean error.
 * Poll the returned task with fetchChatTaskStatus() instead.
 */
export async function startChatTask(
    query: string,
    token: string,
    options: { history?: ChatMessage[]; messageHistoryRaw?: string | null } = {},
): Promise<ChatTaskHandle> {
    const response = await fetch(`${getGrampsApiBase()}/chat/?background=true`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            query,
            history: options.history,
            message_history_raw: options.messageHistoryRaw ?? null,
        }),
    });
    if (!response.ok) {
        throw new ChatError(await readErrorMessage(response));
    }
    const payload = await response.json() as { task: ChatTaskHandle };
    return payload.task;
}

export async function fetchChatTaskStatus(taskId: string, token: string): Promise<ChatTaskStatus> {
    const response = await fetch(`${getGrampsApiBase()}/tasks/${encodeURIComponent(taskId)}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        throw new ChatError(await readErrorMessage(response));
    }
    return await response.json() as ChatTaskStatus;
}
